// Mock implementation of:
//   GET /api/secure/me/today
//
// Wire shape mirrors `TodayResponse.java` (javaserver) exactly so the cutover
// from mocks → real backend is a flag flip per resource.
//
// Mocks are profile-aware: the data shape adapts to currentProfileId so the
// multi-patient model is demonstrable end-to-end. Real backend uses the
// patientId query param for the same effect.

import { delay } from './index'
import { buildCardSync as buildMoodCard, ensureSeed as ensureMoodSeed } from './moodApi'

const STORAGE_KEY = 'patientPortal.mock.todayState.v1'

// Helpers to compute "today" relative times so mock data always feels live.
function todayAt(hh, mm = 0) {
  const d = new Date()
  d.setHours(hh, mm, 0, 0)
  return d.toISOString()
}

function nowPlusMin(min) {
  return new Date(Date.now() + min * 60 * 1000).toISOString()
}

function readMutableState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch (e) {
    return {}
  }
}

function writeMutableState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch (e) {
    // ignore
  }
}

// --- Per-profile rich payloads ----------------------------------------------

function selfProfilePayload(profileId) {
  const state = readMutableState()[profileId] || {}
  return {
    crisisBanner: null,
    dues: {
      totalAmountMinor: 350000, // ₹3,500
      currency: 'INR',
      itemCount: 1,
      headlineLabel: 'Pay ₹3,500 to unlock your next 4 sessions',
    },
    nextSession: {
      eventId: 'evt_self_next',
      // /l/{slug} is the canonical live-session route — slug is the
      // booked-event slug set during booking creation. Mocks carry both id
      // and slug so the frontend's slug-first fallback path is exercisable.
      slug: 'meera-therapy-2026-05-05-am',
      practitionerId: 'prac_dr_meera',
      practitionerName: 'Dr. Meera Iyer',
      sessionType: 'therapy',
      title: 'Therapy session',
      scheduledAt: nowPlusMin(28),
      durationMinutes: 50,
      status: 'SCHEDULED',
      canJoinNow: true,
      canManage: true,
      agendaPrompt: "Let's revisit the sleep hygiene plan and how this week went.",
      sessionMode: 'ONLINE',
      joinUrl: '/l/meera-therapy-2026-05-05-am',
    },
    upcomingSessions: [
      {
        // In-clinic session later today — drives the ClinicQueueCard demo.
        // sessionMode='OFFLINE' has no joinUrl; the queue card surfaces the
        // current chamber position + approx wait every 30s.
        eventId: 'evt_self_clinic_today',
        slug: 'kapoor-psych-review-2026-05-05-pm',
        practitionerId: 'prac_dr_kapoor',
        practitionerName: 'Dr. Sanjay Kapoor',
        sessionType: 'consultation',
        title: 'Psychiatry review',
        scheduledAt: nowPlusMin(150),
        durationMinutes: 25,
        status: 'SCHEDULED',
        canJoinNow: false,
        canManage: true,
        sessionMode: 'OFFLINE',
        locationName: 'Aumhum clinic — Indiranagar',
      },
      {
        eventId: 'evt_self_tomorrow',
        slug: 'neha-nutrition-followup-2026-05-06',
        practitionerId: 'prac_dr_neha',
        practitionerName: 'Dr. Neha Bose',
        sessionType: 'consultation',
        title: 'Nutrition follow-up',
        scheduledAt: nowPlusMin(60 * 22),
        durationMinutes: 30,
        status: 'SCHEDULED',
        canJoinNow: false,
        canManage: true,
        sessionMode: 'ONLINE',
      },
    ],
    // Earlier-today section — drives the SessionsTimelineCard "Earlier
    // today" subsection. Two demo rows: a completed therapy (paid, receipt
    // available) and a no-show consult (no receipt, but visible so the
    // patient can see the recorded miss).
    pastSessionsToday: [
      {
        eventId: 'evt_self_today_past_1',
        slug: 'meera-therapy-2026-05-05-early',
        practitionerId: 'prac_dr_meera',
        practitionerName: 'Dr. Meera Iyer',
        sessionType: 'therapy',
        title: 'Therapy session',
        scheduledAt: nowPlusMin(-180), // 3h ago
        durationMinutes: 50,
        status: 'COMPLETED',
        canJoinNow: false,
        canManage: false,
        sessionMode: 'ONLINE',
        paidAmountMinor: 250000,
        originalPriceMinor: 250000,
        currency: 'INR',
        paymentMode: 'UPI',
        receiptAvailable: true,
      },
      {
        eventId: 'evt_self_today_past_2',
        slug: 'rao-consult-2026-05-05-noshow',
        practitionerId: 'prac_dr_rao',
        practitionerName: 'Dr. Rao',
        sessionType: 'consultation',
        title: 'Quick consult',
        scheduledAt: nowPlusMin(-90), // 1.5h ago
        durationMinutes: 15,
        status: 'NO_SHOW',
        canJoinNow: false,
        canManage: false,
        sessionMode: 'ONLINE',
        receiptAvailable: false,
      },
    ],
    medicinesDueToday: [
      {
        medicineId: 'med_ssri',
        name: 'Sertraline',
        doseLabel: '50mg',
        scheduledTimeLabel: todayAt(8, 0),
        status: state.med_ssri_morning_taken ? 'TAKEN' : 'PENDING',
        isOverdue: !state.med_ssri_morning_taken && new Date().getHours() >= 9,
      },
      {
        medicineId: 'med_ssri',
        name: 'Sertraline',
        doseLabel: '50mg',
        scheduledTimeLabel: todayAt(20, 0),
        status: 'PENDING',
        isOverdue: false,
      },
      {
        medicineId: 'med_b12',
        name: 'Vitamin B12',
        doseLabel: '1 tablet',
        scheduledTimeLabel: todayAt(8, 0),
        status: state.med_b12_taken ? 'TAKEN' : 'PENDING',
        isOverdue: !state.med_b12_taken && new Date().getHours() >= 10,
      },
    ],
    mealsToday: [
      { slot: 'BREAKFAST', slotLabel: 'Breakfast', logged: !!state.meal_breakfast, summary: state.meal_breakfast || null },
      { slot: 'LUNCH', slotLabel: 'Lunch', logged: !!state.meal_lunch, summary: state.meal_lunch || null },
      { slot: 'SNACK', slotLabel: 'Snack', logged: false },
      { slot: 'DINNER', slotLabel: 'Dinner', logged: false },
    ],
    activitiesToday: [
      {
        activityId: 'act_journal',
        title: 'Evening journal: 3 things',
        practitionerName: 'Dr. Meera Iyer',
        completed: !!state.act_journal_done,
        estimatedDurationLabel: '5 min',
      },
      {
        activityId: 'act_walk',
        title: '20-minute outdoor walk',
        practitionerName: 'Dr. Neha Bose',
        completed: !!state.act_walk_done,
        estimatedDurationLabel: '20 min',
      },
    ],
    vitalsToLog: [
      { metric: 'SLEEP_HOURS', label: 'Sleep last night', loggedToday: !!state.vital_sleep },
    ],
    // The mood card now carries options + factors + today's check-in +
    // last-7-day trend so it renders in one round-trip. Source-of-truth for
    // the *user's* entries lives in moodApi's storage, not this `state` blob.
    // Self-profile gets a small synthetic 6-day seed so the sparkline isn't
    // empty on first load — the patient's own check-ins overlay onto today.
    moodCheckIn: (() => {
      ensureMoodSeed(profileId, ['OK', 'GOOD', 'OFF', 'OK', 'GOOD', 'GREAT'])
      return buildMoodCard({ id: profileId, relation: 'SELF' })
    })(),
    unread: { messages: 1, notifications: 0 },
    packProgress: [
      {
        packId: 'pack_meera_8',
        practitionerName: 'Dr. Meera Iyer',
        sessionsUsed: 5,
        sessionsTotal: 8,
        expiryLabel: 'Valid until Jul 15',
      },
    ],
    goalsNudge: [
      {
        goalId: 'goal_sleep',
        title: 'Sleep 7+ hours nightly',
        progressPct: 60,
        microCopy: '4 of 7 nights this week',
      },
    ],
  }
}

function familyProfilePayload(profileId, profile) {
  // Mom profile — chronic-condition flavour.
  const state = readMutableState()[profileId] || {}
  return {
    crisisBanner: null,
    dues: null,
    nextSession: {
      eventId: 'evt_mom_next',
      slug: 'rao-endo-followup-2026-05-05',
      practitionerId: 'prac_dr_rao',
      practitionerName: 'Dr. Rao (endocrinology)',
      sessionType: 'consultation',
      title: 'Endocrine follow-up',
      scheduledAt: nowPlusMin(180),
      durationMinutes: 30,
      status: 'SCHEDULED',
      canJoinNow: false,
      canManage: true,
      sessionMode: 'OFFLINE',
      locationName: 'Aumhum clinic — Koramangala',
    },
    upcomingSessions: null,
    medicinesDueToday: [
      {
        medicineId: 'med_metformin',
        name: 'Metformin',
        doseLabel: '500mg',
        scheduledTimeLabel: todayAt(8, 30),
        status: state.med_metformin_morning_taken ? 'TAKEN' : 'PENDING',
        isOverdue: !state.med_metformin_morning_taken && new Date().getHours() >= 10,
      },
      {
        medicineId: 'med_metformin',
        name: 'Metformin',
        doseLabel: '500mg',
        scheduledTimeLabel: todayAt(20, 30),
        status: 'PENDING',
        isOverdue: false,
      },
      {
        medicineId: 'med_amlodipine',
        name: 'Amlodipine',
        doseLabel: '5mg',
        scheduledTimeLabel: todayAt(8, 30),
        status: state.med_amlodipine_taken ? 'TAKEN' : 'PENDING',
        isOverdue: !state.med_amlodipine_taken && new Date().getHours() >= 10,
      },
    ],
    mealsToday: [
      { slot: 'BREAKFAST', slotLabel: 'Breakfast', logged: !!state.meal_breakfast, summary: state.meal_breakfast || null },
      { slot: 'LUNCH', slotLabel: 'Lunch', logged: false },
      { slot: 'DINNER', slotLabel: 'Dinner', logged: false },
    ],
    activitiesToday: [
      {
        activityId: 'act_physio',
        title: 'Knee strengthening — set of 3',
        practitionerName: 'Physio Anjali',
        completed: !!state.act_physio_done,
        estimatedDurationLabel: '15 min',
      },
    ],
    vitalsToLog: [
      { metric: 'BP', label: 'Blood pressure (morning)', loggedToday: !!state.vital_bp },
      { metric: 'WEIGHT', label: 'Weight', loggedToday: !!state.vital_weight },
    ],
    // Family-profile demo: a quieter trend (3 days seeded) so the
    // chronic-condition mom example still gets a card. The card *is*
    // shown so the caregiver can log on her behalf.
    moodCheckIn: (() => {
      ensureMoodSeed(profileId, [null, null, null, 'OK', 'OK', 'OFF'])
      return buildMoodCard({ id: profileId, relation: 'OTHER' })
    })(),
    unread: { messages: 0, notifications: 1 },
    packProgress: null,
    goalsNudge: [
      {
        goalId: 'goal_bp',
        title: 'BP under 130/85',
        progressPct: 40,
        microCopy: '3 of 7 readings this week within range',
      },
    ],
  }
}

// --- Public mock surface -----------------------------------------------------

export async function getToday(profile) {
  await delay()
  if (!profile) return emptyPayload()
  const isSelf = profile.relation === 'SELF'
  return isSelf ? selfProfilePayload(profile.id) : familyProfilePayload(profile.id, profile)
}

function emptyPayload() {
  return {
    crisisBanner: null, dues: null, nextSession: null, upcomingSessions: null,
    pastSessionsToday: null,
    medicinesDueToday: null, mealsToday: null, activitiesToday: null,
    vitalsToLog: null, moodCheckIn: null, unread: null,
    packProgress: null, goalsNudge: null,
  }
}

// Quick-action mutators used by Today widgets when the user taps a card. These
// patch the per-profile state blob so subsequent /today calls reflect the
// change. Real backend has dedicated endpoints (POST /me/medicines/:id/intake,
// POST /me/diet/log, etc.); these stubs let the F2 demo feel reactive.

export async function quickMutate(profile, key, value = true) {
  await delay(80)
  const all = readMutableState()
  const prof = all[profile.id] || {}
  prof[key] = value
  all[profile.id] = prof
  writeMutableState(all)
  return prof
}

export function __resetTodayMockState() {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (e) {
    /* ignore */
  }
}
